import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { IndividualMessage } from "@/Common/Api/Communications/Dto/IndividualMessage";
import { SaveSendMessageRequest } from "@/Common/Api/Communications/Dto/SaveSendMessageRequest";
import { SaveSendMessageResponse } from "@/Common/Api/Communications/Dto/SaveSendMessageResponse";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EmployeeHelper } from "@/Common/Helpers/EmployeeHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { FileType } from "@/Common/Models/FileType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { CommunicationStateType, MessageOrTaskContext, MessageOrTaskEditContext } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LoadMessageTaskViewResponse } from "@/Communications/Api/Dto/Responses/LoadMessageTaskViewResponse";
import { IndividualDTO, MessageOrTaskItem } from "@/Communications/Models/MessageOrTaskItem";
import { TaskGroupType } from "@/Communications/Models/Tasks/Enums/TaskGroupType";
import { TempLightVacation } from "@/Communications/Models/TempLightVacation";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileDisplayOption, ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { MessageEditComponent } from "./MessageEditComponent";

interface IMessageTaskEditContainerProps {
    tempAttachedLabFillerID?: string;
    isTask: boolean;

    /**
     * Whether to show an overlay behind the edit container (e.g. if it's being shown as a popup).
     */
    useOverlay?: boolean;
    selectedPatients?: Person[];
    messageTaskModel?: MessageOrTaskItem;
    messageTaskResponse?: LoadMessageTaskViewResponse;
    context: MessageOrTaskContext;
    editContext?: MessageOrTaskEditContext;

    onResponseCallback?: (shouldReloadParent: boolean, subject?: string, patientContactId?: string) => void;
    onSaveOverride?: (requestData: SaveSendMessageRequest) => Promise<SaveSendMessageResponse>;

    savedState?: MessageTaskEditContainerState;

    encounterId?: string;

    attachedObjects?: ExportableFile[];

    tempDocumentId?: string;
    selectedClinicians?: Person[];
    clickableTextCssClassName?: string;
}

class MessageTaskEditContainerState {
    public messageId: string;

    public selectedClinicians: Person[];
    public selectedPatients: Person[];
    public subject: string;
    public isSubjectClickableTextShown: boolean;
    public priorities: FlatLookupView[];
    public selectedPriorityId: string;
    public isBodyClickableTextShown: boolean;
    public body: string;

    public selectedTaskGroupTypeId: string;
    public taskStartDate: Date;
    public taskDueDate: Date;

    public isAttachmentChecked: boolean;

    public employeesWithVacation: Person[];
    public isVacationPopupShown: boolean;

    public isAttachmentsPopupOpen: boolean;
    public selectedAttachments: ExportableFileModel[];
    public encounterId?: string;

    public currentFile: AttachedObjectResponse;

    public isAttachingLabToTask: boolean;

    public isSendLocked: boolean;

    private static GetPersonFromIndividualDto(dto: IndividualDTO): Person {
        const person = new Person();
        person.ContactId = dto.Id;
        person.FirstName = dto.FirstName;
        person.LastName = dto.LastName;

        const employee = cacheStore.GetEmployeeByContactIdOrDefault(dto.Id);
        if (employee) {
            if (employee.PrefixLookupId)
                person.prefix = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);
            person.ContactId = employee.Contact_id;
            person.ContactFacetId = employee.ContactFacet_id;
            person.PrefixLookupId = employee.PrefixLookupId;
        }

        return person;
    }

    public static getDefault(): MessageTaskEditContainerState {
        const state = new MessageTaskEditContainerState();

        state.messageId = GuidHelper.Empty();
        state.selectedClinicians = [];
        state.selectedPatients = [];
        state.priorities = cacheStore.FindLookupsByTypeCode("TaskPrority");
        state.selectedPriorityId = cacheStore.FindLookupByLookupCode("Normal", "TaskPrority").LookupID;
        state.selectedTaskGroupTypeId = MessageTaskEditContainerState.getTaskGroupingTypes()[0].Key;
        state.employeesWithVacation = [];
        state.subject = "";
        state.body = "";
        state.selectedAttachments = [];
        state.isSendLocked = false;

        return state;
    }

    public static getTaskGroupingTypes(): KeyValuePair<string, string>[] {
        return [
            new KeyValuePair(TaskGroupType.group, cacheStore.GetString("GroupTaskLabel")),
            new KeyValuePair(TaskGroupType.individual, cacheStore.GetString("IndividualTaskLabel"))
        ];
    }

    public static fromMessageTaskModel(messageTaskItem: MessageOrTaskItem): MessageTaskEditContainerState {
        const state = this.getDefault();
        state.messageId = messageTaskItem.IM_ID;
        state.selectedClinicians = messageTaskItem.ToContact.map(x => this.GetPersonFromIndividualDto(x));
        state.body = messageTaskItem.Body;
        state.subject = messageTaskItem.Subject;
        state.selectedPriorityId = messageTaskItem.PriorityLookupId;
        state.taskStartDate = messageTaskItem.StartDate;
        state.taskDueDate = messageTaskItem.DueDate;
        state.selectedTaskGroupTypeId = messageTaskItem.IsGroupTask ? TaskGroupType.group : TaskGroupType.individual;

        if (messageTaskItem.PatientLinks) {
            state.selectedPatients = messageTaskItem.PatientLinks
                .map(p => this.GetPersonFromIndividualDto(p));
        }

        return state;
    }

    public static fromMessageTaskResponse(messageTaskResponse: LoadMessageTaskViewResponse, context: MessageOrTaskEditContext, prefilledClinicians: Person[]): MessageTaskEditContainerState {
        const state = this.getDefault();

        const formattedDate = moment(messageTaskResponse.IndividualMessage.SendDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(messageTaskResponse.IndividualMessage.SendDate).format("HH:mm:ss");
        const fromContactPerson = cacheStore.GetCliniciansPersonsByContactId(messageTaskResponse.IndividualMessage.FromContactId);

        const bodyContent = `\n\n---------------------------------------------\n${formattedDate}     ${fromContactPerson.Name}\nSubject: ${messageTaskResponse.IndividualMessage.Subject}\n\n${messageTaskResponse.IndividualMessage.Body}`;

        switch (context) {
            case MessageOrTaskEditContext.Forward:
                state.selectedClinicians = prefilledClinicians;
                state.taskStartDate = messageTaskResponse.IndividualMessage.StartDate ? messageTaskResponse.IndividualMessage.StartDate : null;
                state.taskDueDate = messageTaskResponse.IndividualMessage.DueDate ? messageTaskResponse.IndividualMessage.DueDate : null;
                break;
            case MessageOrTaskEditContext.Reply:
                state.selectedClinicians = [fromContactPerson];
                break;
            case MessageOrTaskEditContext.ReplyAll:
                const recipients = messageTaskResponse?.ToClinicians
                    ?.map(x => this.GetPersonFromIndividualDto(x))
                    ?.filter(person => person.ContactFacetId !== fromContactPerson.ContactFacetId) ?? [];
                recipients.push(fromContactPerson);

                state.selectedClinicians = recipients;
                break;
        }

        state.body = bodyContent;
        state.subject = messageTaskResponse.IndividualMessage.Subject;
        state.selectedPriorityId = messageTaskResponse.IndividualMessage.PriorityLookupId;

        if (messageTaskResponse.LinkedPatient)
            state.selectedPatients = [Person.FromIndividual(messageTaskResponse.LinkedPatient.Individual)];

        return state;
    }
}

class MessageTaskEditContainer extends BaseReactComponent<IMessageTaskEditContainerProps, MessageTaskEditContainerState> {
    private _helper: MyleHelper = cacheStore.Helper;

    constructor(props: IMessageTaskEditContainerProps) {
        super(props);
        this.state = this.getInitialState(props);
    }

    private getInitialState(props: IMessageTaskEditContainerProps): MessageTaskEditContainerState {
        if (props.savedState) {
            return props.savedState;
        }
        else {
            let state = MessageTaskEditContainerState.getDefault();
            switch (this.props.context) {
                case MessageOrTaskContext.Communications:
                    if (props.messageTaskModel)
                        state = MessageTaskEditContainerState.fromMessageTaskModel(props.messageTaskModel);
                    else if (props.messageTaskResponse)
                        state = MessageTaskEditContainerState.fromMessageTaskResponse(props.messageTaskResponse, props.editContext, props.selectedClinicians);
                    else
                        state.selectedClinicians = props.selectedClinicians;
                    if (!IsNullOrWhiteSpace(this.props.tempAttachedLabFillerID)) {
                        state.isAttachingLabToTask = true;
                    }
                    break;
                case MessageOrTaskContext.Documents:
                    state.isAttachmentChecked = true;
                    state.selectedPatients = props.selectedPatients;
                    state.selectedClinicians = props.selectedClinicians;
                    break;
                case MessageOrTaskContext.Labs:
                    state.isAttachmentChecked = true;
                    state.selectedPatients = props.selectedPatients;
                    state.selectedClinicians = props.selectedClinicians;
                    state.isAttachingLabToTask = true;
                    break;
                case MessageOrTaskContext.Encounter:
                    state.encounterId = props.encounterId;
                    state.selectedPatients = props.selectedPatients;
                    state.selectedClinicians = props.selectedClinicians;
                    break;
                case MessageOrTaskContext.PopulationBasedFunding:
                case MessageOrTaskContext.Unspecified:
                    state.selectedPatients = props.selectedPatients;
                    state.selectedClinicians = props.selectedClinicians;
                    break;
            }
            if (props.attachedObjects) {
                props.attachedObjects.map(exportableFile => {
                    state.selectedAttachments.push(exportableFile.toExportableFileModel(ExportableFileDisplayOption.NAME));
                });
            }

            return state;
        }
    }

    private resetState(props: IMessageTaskEditContainerProps): void {
        this.setState(props.savedState ?? this.getInitialState(props));
    }

    public componentDidMount(): void {
        if (this.props.useOverlay)
            overlayStore.showOverlay("MessageTaskEditContainer");

        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: IMessageTaskEditContainerProps): void {
        if (prevProps.messageTaskModel != this.props.messageTaskModel)
            this.resetState(this.props);
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay("MessageTaskEditContainer");

        if (this.props.context === MessageOrTaskContext.Communications) {
            setTimeout(() => sessionStore.dispatch(
                LocalOperations.Communications.CommunicationStatesSave,
                {
                    type: CommunicationStateType.MessageTaskEdit,
                    state: this.state
                }
            ), 0);
        }
    }

    public render(): JSX.Element {
        const cssClass = this.props.clickableTextCssClassName ?? "";
        return (
            <MessageEditComponent
                context={this.props.context}
                clickableTextCssClass={cssClass}
                isTask={this.props.isTask}
                model={this.state}
                tempAttachedLabFillerId={this.props.tempAttachedLabFillerID}
                isVacationPopupShown={this.state.isVacationPopupShown}
                employeesInVacation={this.state.employeesWithVacation}
                onSubjectChange={subject => this.mutateState(s => s.subject = subject)}
                onBodyChange={body => this.mutateState(s => s.body = body)}
                onPriorityChange={priority => this.mutateState(s => s.selectedPriorityId = priority)}
                onStartDateChange={startDate => this.startDateChanged(startDate)}
                onDueDateChange={dueDate => this.dueDateChanged(dueDate)}
                onStartTimeChange={startTime => this.startTimeChanged(startTime)}
                onDueTimeChange={dueTime => this.dueTimeChanged(dueTime)}
                onTypeSelectionChange={value => this.mutateState(s => s.selectedTaskGroupTypeId = value)}
                toggleClickableSubjectVisibility={then => this.toggleClickableSubjectVisibility(then)}
                toggleClickableBodyVisibility={then => this.toggleClickableBodyVisibility(then)}
                onSaveDraftClick={() => this.onSaveDraftClickAsync()}
                onCancelClick={() => this.props.onResponseCallback(false)}
                onSendClick={() => this.onSendClick()}
                onVacationPopupCancelClick={() => this.mutateState(s => s.isVacationPopupShown = false)}
                onVacationPopupSendClick={() => this.mutateState(s => s.isSendLocked = true, () => this.sendMessageAsync())}
                onAttachmentClick={() => this.mutateState(s => s.isAttachmentChecked = !s.isAttachmentChecked)}
                openAttachmentPopup={() => this.mutateState(s => s.isAttachmentsPopupOpen = !s.isAttachmentsPopupOpen)}
                onAttachDocumentClick={selectedFiles => {
                    this.mutateState(s => {
                        s.isAttachmentsPopupOpen = false;
                        let newFiles = selectedFiles
                            .filter(x => s.selectedAttachments.all(a => !x.isEquivalent(a.Model)))
                            .map(x => x.toExportableFileModel(ExportableFileDisplayOption.NAME));
                        s.selectedAttachments = s.selectedAttachments.concat(newFiles);
                    });
                }}
                removeAttachmentFromList={itemId => {
                    this.mutateState(s => s.selectedAttachments = s.selectedAttachments.filter(x => x.itemId !== itemId));
                }}
                isAttachingLabToTask={this.state.isAttachingLabToTask}
                onAttachResultToTaskClick={() => this.mutateState(s => s.isAttachingLabToTask = !s.isAttachingLabToTask)}
                selectClinician={clinician => this.selectClinician(clinician)}
                removeClinician={clinician => {
                    if (!clinician || !clinician.Item)
                        return;

                    this.mutateState(s => s.selectedClinicians = s.selectedClinicians.filter(x => x.ContactId != clinician.Item.ContactId));
                }}
                selectPatient={async patient => {
                    if (!patient || !patient.Item)
                        return;

                    let response = await Client.postAsync<LoadPatientModel>(ServerOperations.Server.Myle_LoadPatient, {
                        IndividualId: patient.Item.ContactId
                    });
                    let person = Person.FromPatient(Patient.fromResponseData(response.data));

                    this.mutateState(s => s.selectedPatients.push(person));
                }}
                removePatient={patient => {
                    if (!patient || !patient.Item)
                        return;

                    this.mutateState(s => {
                        s.selectedPatients = s.selectedPatients.filter(x => x.ContactId != patient.Item.ContactId);
                        s.selectedAttachments = [];
                    });
                }}
                isSendLocked={this.state.isSendLocked}
            />
        );
    }

    private isDateEqualTodayWithoutTime(date: Date): boolean {
        let dateWithoutTime = new Date(date);
        dateWithoutTime.setHours(0);
        dateWithoutTime.addMinutes(0);
        dateWithoutTime.setSeconds(0);

        let dateNow = new Date();
        dateNow.setHours(0);
        dateNow.addMinutes(0);
        dateNow.setSeconds(0);

        return dateWithoutTime.getFullYear() === dateNow.getFullYear()
            && dateWithoutTime.getMonth() === dateNow.getMonth()
            && dateWithoutTime.getDate() === dateNow.getDate();
    }

    // update the start Date on the previously stored startDate
    private startDateChanged(date: Date): void {
        this.mutateState(s => {
            if (this.isDateEqualTodayWithoutTime(date)) {
                s.taskStartDate = DateTime.nowDate();
                return;
            }

            let hour = 0;
            let min = 0;

            if (s.taskStartDate) {
                s.taskStartDate = new Date(s.taskStartDate.toString());
                hour = s.taskStartDate.getHours();
                min = s.taskStartDate.getMinutes();
            }
            s.taskStartDate = date;
            s.taskStartDate?.setHours(hour);
            s.taskStartDate?.setMinutes(min);
        });
    }

    // update the due Date on the previously stored dueDate
    private dueDateChanged(date: Date): void {
        this.mutateState(s => {
            if (this.isDateEqualTodayWithoutTime(date)) {
                s.taskDueDate = DateTime.nowDate();
                return;
            }

            let hour = 0;
            let min = 0;

            if (s.taskDueDate) {
                s.taskDueDate = new Date(s.taskDueDate.toString());
                hour = s.taskDueDate.getHours();
                min = s.taskDueDate.getMinutes();
            }
            s.taskDueDate = date;
            s.taskDueDate?.setHours(hour);
            s.taskDueDate?.setMinutes(min);
        });
    }

    // update the start time on the previously stored startDate
    private startTimeChanged(date: Date): void {
        this.mutateState(s => {
            if (date) {
                date = new Date(date.toString());
                s.taskStartDate = s.taskStartDate ? new Date(s.taskStartDate?.toString()) : DateTime.nowDate();
                s.taskStartDate.setHours(date.getHours());
                s.taskStartDate.setMinutes(date.getMinutes());
            }
        });
    }

    // update the due time on the previously stored duetDate
    private dueTimeChanged(date: Date): void {
        this.mutateState(s => {
            if (date) {
                date = new Date(date.toString());
                s.taskDueDate = s.taskDueDate ? new Date(s.taskDueDate?.toString()) : DateTime.nowDate();
                s.taskDueDate.setHours(date.getHours());
                s.taskDueDate.setMinutes(date.getMinutes());
            }
        });
    }

    private selectClinician(clinician: PersonDropdownItem): void {
        this.mutateState(s => EmployeeHelper.selectClinician(s.selectedClinicians, clinician));
    }

    private toggleClickableSubjectVisibility(then: () => void): void {
        this.mutateState(
            s => {
                s.isSubjectClickableTextShown = !s.isSubjectClickableTextShown;
                s.isBodyClickableTextShown = false;
            },
            () => then && then()
        );
    }

    private toggleClickableBodyVisibility(then: () => void): void {
        this.mutateState(
            s => {
                s.isBodyClickableTextShown = !s.isBodyClickableTextShown;
                s.isSubjectClickableTextShown = false;
            },
            () => then && then()
        );
    }

    private async onSaveDraftClickAsync(): Promise<void> {
        let message = new IndividualMessage();
        message.Id = this.state.messageId;
        message.Body = this.state.body;
        message.PriorityLookupId = this.state.selectedPriorityId;
        message.Subject = this.state.subject;
        message.FromContactId = sessionStore.individualContactId;

        if (this.props.isTask) {
            message.TypeTaskLookupId = "";
            message.IsGroupTask = this.state.selectedTaskGroupTypeId === "group";
            message.DueDate = this._helper.CreateDateAsUTC(this.state.taskDueDate);
            message.StartDate = this._helper.CreateDateAsUTC(this.state.taskStartDate);
            message.IsTask = true;
        }

        let clinicianGroups = this.state.selectedClinicians.filter(x => x.IsPersonGroup);
        let clinicianIds = this.state.selectedClinicians.filter(x => !x.IsPersonGroup).map(x => x.ContactId);
        let patientIds = this.state.selectedPatients.map(x => x.ContactId);

        clinicianGroups.forEach(x => {
            if (x.GroupEmployeeModels && x.GroupEmployeeModels.length > 0) {
                x.GroupEmployeeModels.forEach(ge => {
                    const person = cacheStore.GetCliniciansPersonsByContactFacetId(ge);
                    clinicianIds.push(person.ContactId);
                });
            }
        });

        const requestData = {
            Message: message,
            PatientIds: patientIds,
            PracticiansIds: clinicianIds,
            Attachments: [],
            ActionType: 0, // Save,
            Context: this.props.context,
            PatientAttachments: [],
            DocumentId: this.props.tempDocumentId,
            EncounterId: this.props.encounterId
        };

        if (this.state.isAttachingLabToTask) {
            requestData.Attachments.push({ Id: this.props.tempAttachedLabFillerID, FileType: FileType.LAB });
        }

        requestData.PatientAttachments = this.getAttachments();

        const response = await this.saveSendMessageAsync(requestData);
        if (response?.IsDone)
            this.props.onResponseCallback(false);
    }

    public onSendClick(): void {
        this.mutateState(s => s.isSendLocked = true, () => {
            Client.Post(
                ServerOperations.Server.Myle_Communications_VerifyEmployeesOnVacation,
                {
                    EmployeeIds: this.state.selectedClinicians.map(c => c.ContactFacetId)
                },
                response => {
                    let responseList: TempLightVacation[] = response.IdsAndVacationList;

                    let cliniciansWithVacation = responseList.filter(g => {
                        let isInVacation = g.IsInVacation;
                        if (g.IsInVacation) {
                            if (g.FromVacationDate != null)
                                isInVacation = isInVacation && moment() >= moment(g.FromVacationDate);
                            if (g.ToVacationDate != null)
                                isInVacation = isInVacation && moment() <= moment(g.ToVacationDate);
                        }
                        return isInVacation;
                    }).map(withVacation => withVacation.ContactFacet_id);

                    cacheStore.updateEmployeeCacheForVacation(responseList);

                    const allEmployes = cacheStore.GetCliniciansAsPersons();
                    const employeesWithVacationAsPersons = allEmployes.filter(emp => cliniciansWithVacation.indexOf(emp.ContactFacetId) > -1);

                    if (employeesWithVacationAsPersons.length >= 1) {
                        this.mutateState(s => {
                            s.isVacationPopupShown = true;
                            s.employeesWithVacation = employeesWithVacationAsPersons;
                            s.isSendLocked = false;
                        });
                    }
                    else
                        this.sendMessageAsync();
                    if (cacheStore.ClinicSettings.EnableAutofillRecipientInCommunicationTab) {
                        sessionStore.dispatch(
                            LocalOperations.Communications.SavePrefilledSelectedClinicians,
                            {
                                type: CommunicationStateType.MessageTaskEdit,
                                isTask: this.props.isTask,
                                selectedClinicians: this.state.selectedClinicians
                            }
                        );
                    }
                }
            );
        });
    }

    public async sendMessageAsync(): Promise<void> {
        const message = new IndividualMessage();
        message.Id = this.state.messageId;
        message.Body = this.state.body;
        message.PriorityLookupId = this.state.selectedPriorityId;
        message.Subject = this.state.subject;
        message.FromContactId = sessionStore.individualContactId;

        if (this.props.isTask) {
            message.TypeTaskLookupId = "";
            message.IsGroupTask = this.state.selectedTaskGroupTypeId === "group";
            message.DueDate = this._helper.CreateDateAsUTC(this.state.taskDueDate);
            message.StartDate = this._helper.CreateDateAsUTC(this.state.taskStartDate);
            message.IsTask = true;
        }

        const clinicianGroups = this.state.selectedClinicians.filter(x => x.IsPersonGroup);
        let clinicianIds = this.state.selectedClinicians.map(x => x.ContactId);
        const patientIds = this.state.selectedPatients.map(x => x.ContactId);

        clinicianGroups.forEach(x => {
            if (x.GroupEmployeeModels && x.GroupEmployeeModels.length > 0) {
                x.GroupEmployeeModels.forEach(ge => {
                    const person = cacheStore.GetCliniciansPersonsByContactFacetId(ge);
                    clinicianIds.push(person.ContactId);
                });
            }
        });

        clinicianIds = clinicianIds.distinct();

        const requestData = {
            Message: message,
            PatientIds: patientIds,
            PracticiansIds: clinicianIds,
            Attachments: [],
            ActionType: 1,
            Context: this.props.context,
            EncounterId: "",
            PatientAttachments: [],
            DocumentId: ""
        };

        if (this.state.isAttachingLabToTask) {
            requestData.Attachments.push({ Id: this.props.tempAttachedLabFillerID, FileType: FileType.LAB });
        }
        if (this.props.messageTaskResponse && !isArrayNullOrUndefinedOrEmpty(this.props.messageTaskResponse.LinkedItems)) {
            const linkedItem = this.props.messageTaskResponse.LinkedItems.firstOrDefault();
            if (linkedItem.DocumentId) {
                requestData.Context = MessageOrTaskContext.Documents;
                requestData.DocumentId = linkedItem.DocumentId;
            }
            else if (linkedItem.LabFillerId) {
                requestData.Context = MessageOrTaskContext.Labs;
                if (linkedItem.LabFillerId)
                    requestData.Attachments.push({ Id: linkedItem.LabFillerId, FileType: FileType.LAB });
            }
            else if (linkedItem.EncounterId) {
                requestData.Context = MessageOrTaskContext.Encounter;
                requestData.EncounterId = linkedItem.EncounterId;
            }
        }

        if (!IsNullOrWhiteSpace(this.props.tempDocumentId)) {
            requestData.DocumentId = this.props.tempDocumentId;
        }

        if (!IsNullOrWhiteSpace(this.state.encounterId)) {
            requestData.EncounterId = this.state.encounterId;
        }

        requestData.PatientAttachments = this.getAttachments();

        const response = await this.saveSendMessageAsync(requestData);
        this.mutateState(s => s.isSendLocked = false, () => {
            if (response?.IsDone)
                this.props.onResponseCallback(
                    true,
                    this.state.subject,
                    this.state.selectedPatients.any()
                        ? this.state.selectedPatients.firstOrDefault().ContactId
                        : ""
                );
        });
    }

    private async saveSendMessageAsync(requestData: SaveSendMessageRequest): Promise<SaveSendMessageResponse> {
        if (this.props.onSaveOverride) {
            return await this.props.onSaveOverride(requestData);
        }
        else {
            const response = await Client.postAsync<SaveSendMessageResponse>(
                ServerOperations.Server.Myle_SaveSendMessage,
                requestData
            );

            if (response.isSuccess)
                return response.data;
            else
                console.log(response.errors);
        }
    }

    private getAttachments(): AttachedObjectResponse[] {
        if (!isArrayNullOrUndefinedOrEmpty(this.state.selectedPatients) && this.state.selectedAttachments)
            return this.state.selectedAttachments.map(x => x.ToAttachedObjectResponse());

        return [];
    }
}

export { MessageTaskEditContainer, MessageTaskEditContainerState };
